"use client";

import { useNetworkStatus } from "@/hooks/use-network-status";
import { WifiOff, Wifi, CheckCircle2 } from "lucide-react";
import { useEffect, useState, useRef, useCallback } from "react";

/**
 * Offline banner component
 * Shows subtle indicator when offline (like Facebook, Instagram, Tinder)
 * Shows success message briefly when connection restored
 * 
 * CRITICAL: Only shows after verifying offline status to prevent false positives
 */
export function OfflineBanner() {
  const { isOnline, wasOffline, lastOnlineTime } = useNetworkStatus();
  const [showRestored, setShowRestored] = useState(false);
  const [showOffline, setShowOffline] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const offlineTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasShownOfflineRef = useRef(false);

  // Start offline verification with delay
  const startOfflineVerification = useCallback(() => {
    // Clear any existing timeout
    if (offlineTimeoutRef.current) {
      clearTimeout(offlineTimeoutRef.current);
    }

    // Wait 2-3 seconds before showing offline banner
    // This prevents false positives from brief network hiccups
    offlineTimeoutRef.current = setTimeout(() => {
      // Double-check we're still offline before showing
      // Check navigator.onLine directly (source of truth)
      const stillOffline = typeof navigator !== "undefined" && !navigator.onLine;
      
      if (stillOffline) {
        setShowOffline(true);
        hasShownOfflineRef.current = true;
      }
    }, 2000);
  }, []);

  // Initialize after a short delay to avoid false positives on initial load
  useEffect(() => {
    const initTimer = setTimeout(() => {
      setIsInitialized(true);
      // If we're offline after initialization, start the verification delay
      if (!isOnline) {
        startOfflineVerification();
      }
    }, 2000); // Wait 2 seconds before checking offline status

    return () => clearTimeout(initTimer);
  }, [isOnline, startOfflineVerification]);

  // Handle online/offline changes
  useEffect(() => {
    if (!isInitialized) return;

    if (isOnline) {
      // Clear offline banner if we're online
      if (offlineTimeoutRef.current) {
        clearTimeout(offlineTimeoutRef.current);
        offlineTimeoutRef.current = null;
      }
      
      if (showOffline || hasShownOfflineRef.current) {
        // We were offline and now we're online - show restored message
        setShowOffline(false);
        hasShownOfflineRef.current = false;
        
        if (wasOffline) {
          setShowRestored(true);
          const timer = setTimeout(() => {
            setShowRestored(false);
          }, 3000);
          return () => clearTimeout(timer);
        }
      }
    } else {
      // We're offline - start verification delay
      if (!showOffline && !hasShownOfflineRef.current) {
        startOfflineVerification();
      }
    }
  }, [isOnline, wasOffline, isInitialized, showOffline, startOfflineVerification]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (offlineTimeoutRef.current) {
        clearTimeout(offlineTimeoutRef.current);
      }
    };
  }, []);

  // Don't show anything during initial load or if online
  if (!isInitialized || (isOnline && !showRestored)) {
    return null;
  }

  if (showRestored) {
    return (
      <div className="fixed top-0 left-0 right-0 z-50 bg-green-500 text-white px-4 py-2 text-sm text-center flex items-center justify-center gap-2 transition-all duration-300">
        <CheckCircle2 className="w-4 h-4" />
        <span>Connection restored. Syncing...</span>
      </div>
    );
  }

  // Only show offline banner if we've verified we're actually offline
  if (showOffline) {
    return (
      <div className="fixed top-0 left-0 right-0 z-50 bg-yellow-500 text-white px-4 py-2 text-sm text-center flex items-center justify-center gap-2 transition-all duration-300">
        <WifiOff className="w-4 h-4" />
        <span>You're offline. Some features may be limited.</span>
      </div>
    );
  }

  return null;
}
